import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  EyeIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import Badge, { BadgeTone } from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';

const ESCALATION_TONE: Record<string, BadgeTone> = { critical: 'danger', high: 'warning', medium: 'warning', low: 'info' };
const STATUS_TONE: Record<string, BadgeTone> = { escalated: 'purple', in_progress: 'info', assigned: 'warning', acknowledged: 'warning' };

interface EscalatedReport {
  id: string;
  reportNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  urgency: string;
  createdAt: string;
  assignedToId: string;
  assignedDepartmentId: string;
  categoryName: string;
  departmentName: string;
  assignedTechnicianName: string;
  assignmentId: string;
  assignmentCreatedAt: string;
  hoursOverdue: number;
  escalationLevel: 'low' | 'medium' | 'high' | 'critical';
  address?: string;
}

interface EscalationHistory {
  id: string;
  assignmentId: string;
  escalatedFromId: string;
  escalatedFromName: string;
  escalatedToId: string;
  escalatedToName: string;
  escalatedToRole: string;
  escalationReason: string;
  escalationLevel: string;
  hoursOverdue: number;
  escalatedAt: string;
  createdAt: string;
}

const Escalations: React.FC = () => {
  const [escalatedReports, setEscalatedReports] = useState<EscalatedReport[]>([]);
  const [escalationHistory, setEscalationHistory] = useState<EscalationHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'queue' | 'history'>('queue');
  const [selectedReport, setSelectedReport] = useState<EscalatedReport | null>(null);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');

  useEffect(() => {
    fetchEscalationData();
  }, []);

  const fetchEscalationData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch escalation queue
      const queueResponse = await apiService.get('/assignments/escalation-queue');
      if (queueResponse.success) {
        setEscalatedReports(queueResponse.data || []);
      }

      // Fetch escalation history (we'll need to implement this endpoint)
      // const historyResponse = await apiService.get('/assignments/escalations');
      // if (historyResponse.success) {
      //   setEscalationHistory(historyResponse.data || []);
      // }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch escalation data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualEscalation = async (reportId: string) => {
    if (!escalationReason.trim()) {
      setError('Please provide an escalation reason');
      return;
    }

    try {
      const response = await apiService.post(`/assignments/escalate/${reportId}`, {
        escalationReason: escalationReason.trim()
      });

      if (response.success) {
        setShowEscalationModal(false);
        setEscalationReason('');
        setSelectedReport(null);
        await fetchEscalationData(); // Refresh data
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to escalate report');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Escalation Management"
        subtitle="Monitor and manage escalated reports and assignments"
        actions={
          <Button variant="secondary" icon={ArrowUpIcon} onClick={fetchEscalationData}>
            Refresh
          </Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard label="Total Escalated" value={escalatedReports.length} icon={ExclamationTriangleIcon} tone="red" />
        <StatCard
          label="Critical Level"
          value={escalatedReports.filter((r) => r.escalationLevel === 'critical').length}
          icon={ClockIcon}
          tone="amber"
        />
        <StatCard
          label="Departments Affected"
          value={new Set(escalatedReports.map((r) => r.departmentName)).size}
          icon={BuildingOfficeIcon}
          tone="blue"
        />
        <StatCard
          label="Technicians Involved"
          value={new Set(escalatedReports.map((r) => r.assignedToId)).size}
          icon={UserIcon}
          tone="emerald"
        />
      </div>

      {/* Tabs */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('queue')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'queue'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Escalation Queue ({escalatedReports.length})
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'history'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Escalation History ({escalationHistory.length})
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'queue' ? (
                <div>
                  {escalatedReports.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No escalated reports</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        All reports are being handled within normal timeframes.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {escalatedReports.map((report) => (
                        <div
                          key={report.id}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                  {report.title}
                                </h3>
                                <Badge tone={ESCALATION_TONE[report.escalationLevel] || 'neutral'}>{report.escalationLevel.toUpperCase()}</Badge>
                                <Badge value={report.priority} />
                                <Badge tone={STATUS_TONE[report.status] || 'neutral'} value={report.status} />
                              </div>
                              
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{report.description}</p>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <div>
                                  <span className="font-medium">Report #:</span> {report.reportNumber}
                                </div>
                                <div>
                                  <span className="font-medium">Category:</span> {report.categoryName}
                                </div>
                                <div>
                                  <span className="font-medium">Department:</span> {report.departmentName}
                                </div>
                                <div>
                                  <span className="font-medium">Hours Overdue:</span> {report.hoursOverdue}h
                                </div>
                                <div>
                                  <span className="font-medium">Technician:</span> {report.assignedTechnicianName || 'Unassigned'}
                                </div>
                                <div>
                                  <span className="font-medium">Created:</span> {new Date(report.createdAt).toLocaleDateString()}
                                </div>
                                <div>
                                  <span className="font-medium">Assigned:</span> {new Date(report.assignmentCreatedAt).toLocaleDateString()}
                                </div>
                                <div>
                                  <span className="font-medium">Location:</span> {report.address || 'GPS coordinates available'}
                                </div>
                              </div>
                            </div>
                            
                            <div className="ml-4 flex flex-col gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                icon={ExclamationTriangleIcon}
                                onClick={() => {
                                  setSelectedReport(report);
                                  setShowEscalationModal(true);
                                }}
                              >
                                Escalate
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                icon={EyeIcon}
                                onClick={() => {
                                  window.location.href = `/reports`;
                                }}
                              >
                                View Report
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {escalationHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No escalation history</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Escalation history will appear here once reports are escalated.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {escalationHistory.map((escalation) => (
                        <div
                          key={escalation.id}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                              Escalation #{escalation.id.slice(0, 8)}
                            </h3>
                            <Badge tone={ESCALATION_TONE[escalation.escalationLevel] || 'neutral'}>{escalation.escalationLevel.toUpperCase()}</Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            <div>
                              <span className="font-medium">From:</span> {escalation.escalatedFromName}
                            </div>
                            <div>
                              <span className="font-medium">To:</span> {escalation.escalatedToName} ({escalation.escalatedToRole})
                            </div>
                            <div>
                              <span className="font-medium">Hours Overdue:</span> {escalation.hoursOverdue}h
                            </div>
                            <div>
                              <span className="font-medium">Escalated:</span> {new Date(escalation.escalatedAt).toLocaleDateString()}
                            </div>
                            <div>
                              <span className="font-medium">Created:</span> {new Date(escalation.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div className="bg-white rounded p-3">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Reason:</span>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{escalation.escalationReason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

      {/* Escalation Modal */}
      <Modal
        open={showEscalationModal && !!selectedReport}
        onClose={() => {
          setShowEscalationModal(false);
          setEscalationReason('');
          setSelectedReport(null);
        }}
        title={selectedReport ? `Escalate Report: ${selectedReport.title}` : 'Escalate Report'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEscalationModal(false);
                setEscalationReason('');
                setSelectedReport(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" icon={ExclamationTriangleIcon} onClick={() => selectedReport && handleManualEscalation(selectedReport.id)}>
              Escalate Report
            </Button>
          </>
        }
      >
        <div>
          <label htmlFor="escalationReason" className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Escalation Reason
          </label>
          <textarea
            id="escalationReason"
            rows={3}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            placeholder="Enter the reason for escalation..."
            value={escalationReason}
            onChange={(e) => setEscalationReason(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Escalations;
